/**
 * src/components/dashboard/Parte.jsx
 * El parte: qué pide atención, y nada más.
 *
 * Dirección aprobada por Manuel sobre los mockups del 26-08: el formato «parte»
 * —filetes y sangrado, sin tarjetas— con el recuento grande que venía de la
 * dirección «Instrumentos». La lectura del día va abajo, no arriba.
 *
 * El principio que gobierna todo lo de aquí: cuando no hay nada que atender,
 * esto se queda casi vacío. Si algún día tienta rellenarlo, el relleno es el
 * error — un instrumento que dice lo mismo pase lo que pase no es un
 * instrumento.
 */
import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useResolverAlertaEstado } from '../../hooks/useAdminPanel'
import { horaCL } from '../../lib/horaChile'

const COLOR_SEVERIDAD = {
  alerta: 'var(--color-error)',
  aviso: 'var(--color-warn)',
  info: 'var(--color-running)',
}

const ROTULO_AMBITO = {
  servicio: 'Servicio',
  cliente: 'Cliente',
  negocio: 'Negocio',
}

/**
 * Las filas de NEGOCIO se distinguen de un vistazo (Manuel, 08-09, al encargar
 * el vigía de churn: «facilidad para identificar casos»). Tres señales, y
 * ninguna de ellas es un color nuevo:
 *
 *   · la marca pasa de círculo a cuadrado — el color del punto ya está cogido
 *     por la GRAVEDAD, así que el ámbito se dice con la FORMA. Se lee a un
 *     metro y sobrevive a una captura en blanco y negro.
 *   · un distintivo en tinta sólida delante del título — ni rojo, ni ámbar, ni
 *     coral: esos tres significan ya «grave», «al límite» y «esto se pulsa».
 *     Un cuarto color compitiendo con ellos haría el parte más difícil de leer.
 *   · una banda tenue con filete, para que el ojo las agrupe aunque estén
 *     separadas en la lista.
 *
 * ⚠️ Se marca SOLO el negocio, no las dos familias. Tres de cada cuatro
 * atenciones del parte son técnicas: una etiqueta que llevan todas las filas no
 * distingue nada. Se marca la excepción.
 */
const AMBITO_DESTACADO = 'negocio'

function _hora(iso) {
  return horaCL(iso)
}

/** Una cosa que atender: qué pasa, desde cuándo y a dónde ir. */
// Lo que el vigía midió para emitir el aviso — y sobre todo el «Se arregla»
// que eligió. Hasta el 07-09-2026 esto viajaba solo a Telegram: el aviso de
// `las_pataguas` decía qué pasaba y se callaba qué hacer, aunque el sistema ya
// lo había decidido. Y no es un matiz: «Facturación fuera del catálogo medido»
// elige entre dos remedios OPUESTOS —«falta mercado» o «amplía el catálogo»—
// según lo que midió, y de cuál sea depende que haya que gastar 40 minutos de
// sondeo o ninguno.
//
// Plegado a propósito: quien no lo abre no paga su altura, como la lista de
// productos sin ficha del panel del cliente. Y sin campos NO se dibuja nada —
// un desplegable vacío diría que ese aviso no midió (§3).
function QueMidio({ campos }) {
  const filas = Object.entries(campos || {})
  if (filas.length === 0) return null

  return (
    <details
      onClick={e => e.stopPropagation()}
      style={{ marginTop: 'var(--space-2)' }}
    >
      <summary style={{
        cursor: 'pointer', fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)', color: 'var(--color-accent)',
      }}>
        qué midió el aviso ({filas.length})
      </summary>
      <dl style={{
        margin: 'var(--space-2) 0 0',
        borderLeft: '2px solid var(--color-border)',
        paddingLeft: 'var(--space-4)',
        display: 'grid', gridTemplateColumns: 'auto 1fr',
        gap: 'var(--space-2) var(--space-4)', alignItems: 'baseline',
      }}>
        {filas.map(([k, v]) => (
          <Fragment key={k}>
            <dt style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
              color: 'var(--color-text-3)', textTransform: 'uppercase',
              letterSpacing: '0.04em', whiteSpace: 'nowrap',
            }}>
              {k}
            </dt>
            <dd style={{
              margin: 0, fontSize: 'var(--text-base)',
              color: 'var(--color-text-2)', lineHeight: 1.5,
              overflowWrap: 'anywhere',
            }}>
              {String(v)}
            </dd>
          </Fragment>
        ))}
      </dl>
    </details>
  )
}


function Atencion({ dato }) {
  const navigate = useNavigate()
  const resolver = useResolverAlertaEstado()
  const color = COLOR_SEVERIDAD[dato.severidad] || COLOR_SEVERIDAD.info
  const esNegocio = dato.ambito === AMBITO_DESTACADO

  // «Marcar resuelta» solo en las alertas que el panel no puede re-medir
  // (resolucion 'manual'): a las vivas les basta resolverse — desaparecen
  // solas —, y a las 'auto' las cierra el botón «Actualizar».
  function marcarResuelta(e) {
    e.stopPropagation()
    resolver.mutate(dato.clave)
  }

  return (
    <div
      onClick={() => dato.ruta && navigate(dato.ruta)}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 'var(--space-4)',
        alignItems: 'baseline',
        padding: 'var(--space-4) 0',
        borderBottom: '1px solid var(--color-border)',
        cursor: dato.ruta ? 'pointer' : 'default',
        transition: 'opacity var(--t-base) var(--ease)',
        ...(esNegocio ? {
          paddingLeft: 'var(--space-4)',
          marginLeft: 'calc(-1 * var(--space-4))',
          background: 'var(--color-banda-negocio)',
          boxShadow: 'inset 3px 0 0 var(--color-text)',
        } : null),
      }}
      onMouseEnter={e => { if (dato.ruta) e.currentTarget.style.opacity = '0.7' }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
    >
      <span
        data-marca={esNegocio ? 'negocio' : 'tecnico'}
        style={{
          width: esNegocio ? '8px' : '7px', height: esNegocio ? '8px' : '7px',
          borderRadius: esNegocio ? '2px' : 'var(--radius-pill)',
          background: color, display: 'block', marginTop: '6px', flexShrink: 0,
        }}
      />
      <div>
        <div style={{
          fontSize: 'var(--text-md)', color: 'var(--color-text)',
          fontWeight: 'var(--weight-bold)', lineHeight: 1.35,
        }}>
          {esNegocio && (
            <span style={{
              display: 'inline-block', verticalAlign: '2px',
              marginRight: 'var(--space-2)',
              background: 'var(--color-text)', color: 'var(--color-surface)',
              fontFamily: 'var(--font-mono)', fontSize: '9.5px',
              letterSpacing: '0.13em', textTransform: 'uppercase',
              padding: '2px 6px 1px', borderRadius: '3px',
            }}>
              {ROTULO_AMBITO.negocio}
            </span>
          )}
          {dato.titulo}
        </div>
        <div style={{
          fontSize: 'var(--text-base)', color: 'var(--color-text-2)',
          marginTop: 'var(--space-1)', lineHeight: 1.5,
        }}>
          {dato.detalle}
        </div>
        <QueMidio campos={dato.campos} />
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
          color: 'var(--color-text-3)',
        }}>
          {dato.cuando}
        </div>
        {dato.accion && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
            color: 'var(--color-accent)', marginTop: 'var(--space-1)',
          }}>
            {dato.accion} →
          </div>
        )}
        {dato.resolucion === 'manual' && (
          <button
            onClick={marcarResuelta}
            disabled={resolver.isPending}
            style={{
              marginTop: 'var(--space-2)',
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
              color: 'var(--color-text-3)',
              background: 'transparent',
              border: '1px solid var(--color-border-2)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 8px', cursor: 'pointer',
              transition: 'all var(--t-base) var(--ease)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--color-ok)'
              e.currentTarget.style.borderColor = 'var(--color-ok-line)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--color-text-3)'
              e.currentTarget.style.borderColor = 'var(--color-border-2)'
            }}
          >
            {resolver.isPending ? 'Cerrando…' : '✓ Marcar resuelta'}
          </button>
        )}
      </div>
    </div>
  )
}

/** La banda: el recuento grande y qué significa. */
function Banda({ atenciones, turno, cargando }) {
  const total = atenciones.length
  const averias = atenciones.filter(a => a.severidad === 'alerta').length
  const vuelta = _hora(turno?.ultima_vuelta)
  const sinActividad = turno?.sin_actividad

  if (cargando) {
    return (
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-3)',
      }}>
        Leyendo el estado…
      </div>
    )
  }

  // Sin última vuelta no se puede afirmar que todo corrió: no se ha mirado.
  if (!turno?.ultima_vuelta) {
    return (
      <Cabecera
        rotulo="Sin lectura" color="var(--color-warn)" cuenta={null}
        titulo="Hoy todavía no he mirado"
        sub="El scheduler no registra ninguna vuelta terminada. No es que no haya nada: es que no se ha medido."
      />
    )
  }

  if (total === 0) {
    return (
      <Cabecera
        rotulo="Todo en orden" color="var(--color-ok)" cuenta={null}
        titulo="Todo corrió"
        sub={`La última vuelta terminó a las ${vuelta} sin incidencias.`}
      />
    )
  }

  const cuerpo = averias > 0
    ? (averias === total
        ? (total === 1 ? 'Es una avería.' : 'Todas son averías.')
        : `${averias} ${averias === 1 ? 'es una avería' : 'son averías'}.`)
    : 'Ninguna es una avería: son avisos.'

  return (
    <Cabecera
      rotulo="Atención"
      color={averias > 0 ? 'var(--color-error)' : 'var(--color-warn)'}
      cuenta={total}
      titulo={total === 1 ? 'Una cosa pide tu atención' : `${total} cosas piden tu atención`}
      sub={`${cuerpo} ${sinActividad
        ? 'Y hace más de un día que no termina una vuelta.'
        : `La última vuelta completa terminó a las ${vuelta}.`}`}
    />
  )
}

function Cabecera({ rotulo, color, cuenta, titulo, sub }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        letterSpacing: '0.14em', textTransform: 'uppercase', color,
      }}>
        {rotulo}
      </div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 'var(--space-4)',
        marginTop: 'var(--space-3)',
      }}>
        {cuenta != null && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: '44px', color,
            lineHeight: 1, fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.03em',
          }}>
            {String(cuenta).padStart(2, '0')}
          </span>
        )}
        <div>
          <h2 style={{
            margin: 0, fontFamily: 'var(--font-brand)',
            fontSize: cuenta != null ? 'var(--text-xl)' : '32px',
            fontWeight: 'var(--weight-bold)', letterSpacing: '-0.03em',
            lineHeight: 1.15, color: 'var(--color-text)', textWrap: 'balance',
          }}>
            {titulo}
          </h2>
          <p style={{
            margin: 'var(--space-2) 0 0', fontSize: 'var(--text-md)',
            color: 'var(--color-text-2)', maxWidth: '60ch', lineHeight: 1.6,
          }}>
            {sub}
          </p>
        </div>
      </div>
    </div>
  )
}

export function Parte({ datos, cargando }) {
  const atenciones = datos?.atenciones ?? []

  return (
    <div style={{ maxWidth: '860px' }}>
      <Banda atenciones={atenciones} turno={datos?.turno} cargando={cargando} />

      {!cargando && atenciones.length > 0 && (
        <div style={{ marginTop: 'var(--space-6)' }}>
          {atenciones.map(a => <Atencion key={a.clave} dato={a} />)}
        </div>
      )}

      {!cargando && atenciones.length > 0 && (
        <div style={{
          marginTop: 'var(--space-3)', fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
        }}>
          {(() => {
            const porAmbito = atenciones.reduce((acc, a) => {
              acc[a.ambito] = (acc[a.ambito] || 0) + 1
              return acc
            }, {})
            return Object.entries(porAmbito)
              .map(([k, n]) => `${n} de ${(ROTULO_AMBITO[k] || k).toLowerCase()}`)
              .join(' · ')
          })()}
        </div>
      )}

      {!cargando && atenciones.length === 0 && datos?.turno?.ultima_vuelta && (
        <div style={{
          marginTop: 'var(--space-6)', padding: 'var(--space-6)',
          border: '1px dashed var(--color-border-2)', borderRadius: 'var(--radius-md)',
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
        }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: 'var(--radius-pill)',
            background: 'var(--color-ok)', display: 'block', flexShrink: 0,
          }} />
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-base)',
            color: 'var(--color-text-2)',
          }}>
            Nada que atender: sin jobs colgados, sin tenants fuera de SLA y sin alertas vigentes.
          </span>
        </div>
      )}

      {/* Las cifras del día viven ahora en la columna derecha (ColumnaCifras):
          decisión de Manuel del 27-08 — cuadros al lado del parte, no un pie
          que obliga a bajar. */}
    </div>
  )
}
